import { useEffect, useState } from 'react';
import '../css/process.css'
import { useNavigate } from "react-router-dom";
import config from '../Context/Config';
import Background from '../Components/background/background';
import { showError } from '../Utils/swal';

const ProcessPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getAccessToken();
    }, []);

    const clearAuthData = () => {
        const keysToRemove = [
            'digital-form-accesstoken',
            'digital-form-idtoken', 
            'digital-form-refresh_token',
            'digital-form-user_details',
            'digital-form-user-id',
            'digital-form-dept-id',
            'digital-form-dept-name',
            'digital-form-dept-practice',
            'digital-form-dept-type',
            'digital-form-expiry',
            'digital-form-login_response',
            'digital-form-locked',
            'defaultLocation'
        ];
        
        keysToRemove.forEach(key => localStorage.removeItem(key));
    };

    const handleAuthError = (message = "An unexpected error occurred. Please try again later.") => {
        showError("Authentication Error", message, { confirmButtonText: "Return to Login" }).then(() => {
            clearAuthData();
            navigate("/");
        });
    };

    const handleAccessDenied = () => {
        showError("Access Denied", "Please contact your administrator for access.", { confirmButtonText: "Return to Login" }).then(() => {
            clearAuthData();
            navigate("/");
        });
    };

    const validateAuthorizationCode = (code) => {
        // Check if code exists and is not empty
        if (!code || typeof code !== 'string' || code.trim() === '') {
            return false;
        }

        // Basic format validation - authorization codes should be alphanumeric with possible hyphens/underscores
        if (code.length < 10 || code.length > 2048) {
            return false;
        }

        // Check for valid characters only
        if (!/^[a-zA-Z0-9\-_.]+$/.test(code)) {
            return false;
        }

        return true;
    };

    const validateUserDetails = (userDetails) => {
        if (!userDetails || typeof userDetails !== 'object') {
            return false;
        }

        const requiredFields = ['user_id', 'dept_id', 'dept_name', 'practice', 'type'];
        return requiredFields.every(field => 
            userDetails.hasOwnProperty(field) && 
            userDetails[field] !== null && 
            userDetails[field] !== undefined &&
            String(userDetails[field]).trim() !== ''
        );
    };

    const validateSessionData = (sessionData) => {
        if (!sessionData || typeof sessionData !== 'object') {
            return false;
        }

        const { session } = sessionData;
        if (!session || typeof session !== 'object') {
            return false;
        }

        const requiredTokens = ['access_token', 'id_token', 'refresh_token', 'expires_at'];
        return requiredTokens.every(token => 
            session.hasOwnProperty(token) && 
            session[token] !== null && 
            session[token] !== undefined &&
            String(session[token]).trim() !== ''
        );
    };

    const storeUserDetailsInLocalStorage = (userDetails) => {
        try {
            if (!validateUserDetails(userDetails)) {
                throw new Error('Invalid user details structure');
            }

            localStorage.setItem("digital-form-user_details", JSON.stringify(userDetails));
            localStorage.setItem("digital-form-user-id", JSON.stringify(userDetails.user_id));
            localStorage.setItem("digital-form-dept-id", String(userDetails.dept_id));
            localStorage.setItem("digital-form-dept-name", String(userDetails.dept_name));
            localStorage.setItem("digital-form-dept-practice", String(userDetails.practice));
            localStorage.setItem("digital-form-dept-type", String(userDetails.type));
            localStorage.setItem("defaultLocation", String(userDetails.dept_id));
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error storing user details:', error);
            }
            throw new Error('Failed to store user information');
        }
    };

    const storeSessionData = (message) => {
        try {
            if (!validateSessionData(message)) {
                throw new Error('Invalid session data structure');
            }

            const { session } = message;
            const expiryTime = Date.now() + (session.expires_at * 1000);

            localStorage.setItem("digital-form-accesstoken", session.access_token);
            localStorage.setItem("digital-form-idtoken", session.id_token);
            localStorage.setItem("digital-form-expiry", String(expiryTime));
            localStorage.setItem("digital-form-login_response", JSON.stringify(message));
            localStorage.setItem("digital-form-refresh_token", session.refresh_token);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error storing session data:', error);
            }
            throw new Error('Failed to store session information');
        }
    };

    const getAccessToken = async () => {
        setLoading(true);
        
        try {
            const url = new URL(window.location.href);
            const code = url.searchParams.get("code");
            const error = url.searchParams.get("error");

            // Check for OAuth error response
            if (error) {
                const errorDescription = url.searchParams.get("error_description") || "Authentication failed";
                handleAuthError(`Authentication error: ${errorDescription}`);
                return;
            }

            // Validate authorization code
            if (!validateAuthorizationCode(code)) {
                handleAuthError("Invalid authorization response. Please try logging in again.");
                return;
            }

            // Make API call to exchange code for tokens
            const response = await fetch(`${config.API_BASE_URL}/auth/callback?code=${encodeURIComponent(code)}`, {
                method: "GET",
                headers: { 
                    "Content-Type": "application/json"
                },
            });

            if (!response.ok) {
                if (response.status === 400) {
                    handleAuthError("Invalid authorization code. Please try logging in again.");
                } else if (response.status === 401) {
                    handleAuthError("Authentication failed. Please try logging in again.");
                } else if (response.status >= 500) {
                    handleAuthError("Server error. Please try again later.");
                } else {
                    handleAuthError("Authentication failed. Please try again.");
                }
                return;
            }

            const message = await response.json();
            
            // Store session data
            storeSessionData(message);
            
            // Get user information
            await getUserInfo();
            
        } catch (error) {
            // Only log detailed errors in development
            if (process.env.NODE_ENV === 'development') {
                console.error("Error in getAccessToken:", error);
            }
            
            handleAuthError("Unable to complete authentication. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getUserInfo = async () => {
        try {
            const idToken = localStorage.getItem("digital-form-idtoken");
            const accessToken = localStorage.getItem("digital-form-accesstoken");

            if (!idToken || !accessToken) {
                throw new Error("Missing authentication tokens");
            }

            // Get user's practice from stored session or use default
            const storedResponse = localStorage.getItem("digital-form-login_response");
            let practice = "chc"; // Default fallback
            
            if (storedResponse) {
                try {
                    const parsedResponse = JSON.parse(storedResponse);
                    // Extract practice from token or session if available
                    practice = parsedResponse.practice || practice;
                } catch (e) {
                    // Use default if parsing fails
                }
            }

            const headers = {
                "Content-Type": "application/json",
                Authorization: idToken,
                action_id: config.ACTIONID['view'],
                practice: practice
            };

            const body = JSON.stringify({
                accesstoken: accessToken,
            });

            const response = await fetch(`${config.API_BASE_URL}/auth/login`, {
                method: "POST",
                headers: headers,
                body: body,
            });

            if (!response.ok) {
                let errorData = {};
                try {
                    errorData = await response.json();
                } catch (e) {
                    // Continue with empty error data if parsing fails
                }
                
                handleApiError(response.status, errorData);
                return;
            }

            const data = await response.json();
            
            if (!data.user_details) {
                throw new Error("No user details received");
            }

            storeUserDetailsInLocalStorage(data.user_details);
            navigate("/daily-appointments");
            
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error("Error in getUserInfo:", error);
            }
            
            handleAuthError("Unable to retrieve user information. Please try logging in again.");
        }
    };

    const handleApiError = (status, errorData) => {
        // Clear auth data on any API error for security
        clearAuthData();
        
        if (status === 401) {
            handleAuthError("Your session has expired. Please log in again.");
        } else if (status === 403) {
            handleAccessDenied();
        } else if (status >= 500) {
            handleAuthError("Server error. Please try again later or contact support.");
        } else {
            handleAuthError("Unable to complete authentication. Please try again.");
        }
    };

    return (
        <div className="relative h-screen w-screen bg-white overflow-hidden">
            {/* Animated Background */}
            <Background />

            {/* Loading Indicator */}
            {loading && (
                <div className="absolute inset-0 flex justify-center items-center z-10">
                    <div className="cloud"></div>
                </div>
            )}
        </div>
    );
};

export default ProcessPage;